// @ts-nocheck
// ============================================
// PDF智能阅读助手 - 标注列表组件
// 版本: 2.0
// 功能: 分类管理、搜索筛选、批量操作
// ============================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import StatisticsPanel from './StatisticsPanel';
import ExportDialog from './ExportDialog';
import {
  SESSION_TYPES,
  SESSION_TYPE_CONFIG,
  SUB_TYPE_CONFIG,
  GUIDE_SUB_TYPES,
  QA_SUB_TYPES,
  MANUAL_SUB_TYPES,
  BLOCK_TYPES,
  getTypeConfig,
  formatTimestamp,
  loadDeletedAnnotationsByFingerprint,
  restoreAnnotationsBatch,
  deleteAnnotationsBatch,
  saveAnnotationsToStorage,
} from './pdfUtils';
import { logger } from '../utils/logger';

interface Annotation {
  id: string;
  sessionType?: string;
  subType?: string;
  isDeleted?: boolean;
  timestamp: string;
  question?: string;
  answer?: string;
  text?: string;
  sessionName?: string;
  page: number;
  type?: string;
  hasHighlight?: boolean;
  aiQuickAnalysis?: string;
  aiDeepAnalysis?: string;
  conversationHistory?: any;
  [key: string]: any;
}

interface PdfInfo {
  fingerprint: string;
  fileName?: string;
  fileSize?: number;
  totalPages?: number;
  [key: string]: any;
}

interface AnnotationsListProps {
  show: boolean;
  annotations: Record<string, Annotation>;
  pdfInfo: PdfInfo | null;
  onClose: () => void;
  onViewAnnotation: (annotation: Annotation) => void;
  onBatchDelete: (ids: string[]) => Promise<boolean>;
  onRefresh?: () => Promise<any>;
  tagFilter?: string;
  onTagFilterChange?: (tag: string) => void;
  [key: string]: any;
}

/**
 * 标注列表组件
 */
export default function AnnotationsList({
  show,
  annotations,
  pdfInfo,
  onClose,
  onViewAnnotation,
  onBatchDelete,
  onRefresh
}: AnnotationsListProps) {
  // ============================================
  // 状态管理
  // ============================================

  const [activeTab, setActiveTab] = useState<string>('guide');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [subTypeFilter, setSubTypeFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('time');

  // 新增：统计和导出状态
  const [showStatistics, setShowStatistics] = useState<boolean>(false);
  const [showExportDialog, setShowExportDialog] = useState<boolean>(false);

  // 新增：已删除标注的独立状态
  const [deletedAnnotations, setDeletedAnnotations] = useState<Record<string, Annotation>>({});

  // 新增：语音生成状态
  const [isGeneratingSpeech, setIsGeneratingSpeech] = useState<boolean>(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const audioCache = React.useRef<Map<string, string>>(new Map());

  // ============================================
  // 副作用：重置选择当列表关闭
  // ============================================

  useEffect(() => {
    if (!show) {
      setSelectedIds(new Set());
      setSearchText('');
    }
  }, [show]);

  // ============================================
  // 副作用：加载已删除标注
  // ============================================

  useEffect(() => {
    const fetchDeletedAnnotations = async () => {
      if (activeTab === 'deleted' && pdfInfo?.fingerprint) {
        try {
          const { annotations } = await loadDeletedAnnotationsByFingerprint(pdfInfo.fingerprint) as any;
          setDeletedAnnotations(annotations);
          logger.log('已加载已删除标注:', Object.keys(annotations).length, '个');
        } catch (error: any) {
          console.error('加载已删除标注失败:', error);
        }
      }
    };

    fetchDeletedAnnotations();
  }, [activeTab, pdfInfo?.fingerprint]);

  // ============================================
  // 调试：打印传入的标注数据
  // ============================================

  useEffect(() => {
    const allAnns = Object.values(annotations);
    logger.log('[AnnotationsList] 收到标注数据:', {
      total: allAnns.length,
      bySessionType: allAnns.reduce((acc: Record<string, number>, ann) => {
        acc[ann.sessionType || 'undefined'] = (acc[ann.sessionType || 'undefined'] || 0) + 1;
        return acc;
      }, {}),
      bySubType: allAnns.reduce((acc: Record<string, number>, ann) => {
        const subType = ann.subType || 'undefined';
        acc[subType] = (acc[subType] || 0) + 1;
        return acc;
      }, {}),
      guideItems: allAnns
        .filter(a => a.sessionType === 'guide')
        .slice(0, 5)
        .map(a => ({
          id: a.id?.substring(0, 8),
          subType: a.subType,
          question: a.question?.substring(0, 30) || '(无)',
          hasAnswer: !!a.answer
        }))
    });
  }, [annotations]);

  // ============================================
  // 按类型分类标注
  // ============================================

  const categorizedAnnotations = useMemo(() => {
    const allAnnotations = Object.values(annotations);

    return {
      guide: allAnnotations.filter(ann =>
        ann.sessionType === (SESSION_TYPES as any).GUIDE &&
        !ann.isDeleted
      ),
      qa: allAnnotations.filter(ann =>
        ann.sessionType === (SESSION_TYPES as any).QA && !ann.isDeleted
      ),
      manual: allAnnotations.filter(ann =>
        ann.sessionType === (SESSION_TYPES as any).MANUAL && !ann.isDeleted
      ),
      deleted: Object.values(deletedAnnotations)
    };
  }, [annotations, deletedAnnotations]);

  // ============================================
  // 获取当前标签页的标注（含筛选和排序）
  // ============================================

  const currentAnnotations = useMemo(() => {
    let anns = (categorizedAnnotations as any)[activeTab] || [];

    // 子类型筛选
    if (subTypeFilter && subTypeFilter !== 'all') {
      anns = anns.filter((ann: Annotation) => ann.subType === subTypeFilter);
    }

    // 搜索筛选
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      anns = anns.filter((ann: Annotation) =>
        ann.question?.toLowerCase().includes(searchLower) ||
        ann.answer?.toLowerCase().includes(searchLower) ||
        ann.text?.toLowerCase().includes(searchLower) ||
        ann.sessionName?.toLowerCase().includes(searchLower)
      );
    }

    // 排序
    if (sortBy === 'time') {
      anns.sort((a: Annotation, b: Annotation) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    } else if (sortBy === 'page') {
      anns.sort((a: Annotation, b: Annotation) => a.page - b.page);
    }

    return anns as Annotation[];
  }, [categorizedAnnotations, activeTab, subTypeFilter, searchText, sortBy]);

  // ============================================
  // 获取子类型筛选选项
  // ============================================

  const getSubTypeOptions = (): { value: string; label: string }[] => {
    switch (activeTab) {
      case 'guide':
        return Object.entries(GUIDE_SUB_TYPES as Record<string, string>).map(([key, value]) => ({
          value,
          label: (SUB_TYPE_CONFIG as any)[value].shortName
        }));
      case 'qa':
        return Object.entries(QA_SUB_TYPES as Record<string, string>).map(([key, value]) => ({
          value,
          label: (SUB_TYPE_CONFIG as any)[value].shortName
        }));
      case 'manual':
        return Object.entries(MANUAL_SUB_TYPES as Record<string, string>).map(([key, value]) => ({
          value,
          label: (SUB_TYPE_CONFIG as any)[value].shortName
        }));
      default:
        return [];
    }
  };

  // ============================================
  // 选择处理函数
  // ============================================

  const handleSelectOne = (annotationId: string) => {
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(annotationId)) {
      newSelectedIds.delete(annotationId);
    } else {
      newSelectedIds.add(annotationId);
    }
    setSelectedIds(newSelectedIds);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === currentAnnotations.length) {
      setSelectedIds(new Set());
    } else {
      const allIds = new Set(currentAnnotations.map(ann => ann.id));
      setSelectedIds(allIds);
    }
  };

  // ============================================
  // 批量删除处理
  // ============================================

  const handleBatchDeleteConfirm = () => {
    if (selectedIds.size === 0) return;
    setShowBatchDeleteConfirm(true);
  };

  const executeBatchDelete = async () => {
    if (selectedIds.size === 0) return;

    setIsDeleting(true);
    try {
      const success = await onBatchDelete(Array.from(selectedIds));
      if (success) {
        setSelectedIds(new Set());
      }
    } catch (error: any) {
      console.error('批量删除失败:', error);
    } finally {
      setIsDeleting(false);
      setShowBatchDeleteConfirm(false);
    }
  };

  const cancelBatchDelete = () => {
    setShowBatchDeleteConfirm(false);
  };

  // ============================================
  // 批量恢复和彻底删除（回收站功能）
  // ============================================

  const [showRestoreConfirm, setShowRestoreConfirm] = useState<boolean>(false);
  const [showPermanentDeleteConfirm, setShowPermanentDeleteConfirm] = useState<boolean>(false);
  const [isRestoring, setIsRestoring] = useState<boolean>(false);
  const [isPermanentDeleting, setIsPermanentDeleting] = useState<boolean>(false);

  // 批判性总结相关状态
  const [showCriticalSummaryDialog, setShowCriticalSummaryDialog] = useState<boolean>(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState<boolean>(false);
  const [summaryTitle, setSummaryTitle] = useState<string>('');
  const [generatedSummary, setGeneratedSummary] = useState<string>('');

  // 生成批判性总结
  const handleGenerateCriticalSummary = useCallback(async () => {
    if (selectedIds.size < 2) {
      alert('请至少选择2条导读记录');
      setShowCriticalSummaryDialog(false);
      return;
    }

    setIsGeneratingSummary(true);
    try {
      const requestData = {
        pdfFingerprint: pdfInfo!.fingerprint,
        annotationIds: Array.from(selectedIds)
      };
      logger.log('[前端] 发送批判性总结请求:', requestData);
      logger.log('[前端] 选中的标注数量:', requestData.annotationIds.length);
      logger.log('[前端] 前5个ID:', requestData.annotationIds.slice(0, 5));

      const response = await fetch('/api/guide/critical-summary-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      const result = await response.json();

      if (result.success) {
        setGeneratedSummary(result.summary);
      } else {
        alert('生成失败: ' + (result.error || '未知错误'));
        setShowCriticalSummaryDialog(false);
      }
    } catch (error: any) {
      console.error('生成批判性总结失败:', error);
      alert('生成失败: ' + error.message);
      setShowCriticalSummaryDialog(false);
    } finally {
      setIsGeneratingSummary(false);
    }
  }, [selectedIds, pdfInfo]);

  // 当对话框打开时自动生成
  useEffect(() => {
    logger.log('[批判性总结] useEffect触发:', {
      showCriticalSummaryDialog,
      hasGeneratedSummary: !!generatedSummary,
      isGeneratingSummary
    });

    if (showCriticalSummaryDialog && !generatedSummary && !isGeneratingSummary) {
      logger.log('[批判性总结] 开始调用handleGenerateCriticalSummary');
      handleGenerateCriticalSummary();
    } else if (showCriticalSummaryDialog) {
      logger.log('[批判性总结] 不调用API，原因:', {
        已有摘要: !!generatedSummary,
        正在生成: isGeneratingSummary
      });
    }

    // 对话框关闭时清空状态
    if (!showCriticalSummaryDialog) {
      setGeneratedSummary('');
      setSummaryTitle('');
    }
  }, [showCriticalSummaryDialog, generatedSummary, isGeneratingSummary, handleGenerateCriticalSummary]);

  // 保存批判性总结（使用新的 blocks API）
  const handleSaveCriticalSummary = async () => {
    if (!generatedSummary) {
      alert('没有生成的总结');
      return;
    }

    if (!pdfInfo || !pdfInfo.fingerprint) {
      alert('PDF信息缺失，无法保存');
      return;
    }

    const title = summaryTitle.trim() || `批判性总结_${new Date().toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;

    try {
      const newAnnotation = {
        pdfFingerprint: pdfInfo.fingerprint,
        page: 0,
        text: '',
        question: `批判性总结：${title}`,
        answer: generatedSummary,
        tag: 'summary',
        sessionType: (SESSION_TYPES as any).GUIDE,
        subType: (GUIDE_SUB_TYPES as any).CRITICAL_SUMMARY,
        sessionName: title,
        annotationType: 'text',
        conversationHistory: {
          baseAnnotationIds: Array.from(selectedIds),
          generatedAt: new Date().toISOString()
        }
      };

      const success = await saveAnnotationsToStorage(pdfInfo.fingerprint, { temp: newAnnotation } as any);

      if (success) {
        if (onRefresh) {
          await onRefresh();
        }
        alert('批判性总结已保存');
        setShowCriticalSummaryDialog(false);
        setSelectedIds(new Set());
      } else {
        alert('保存失败: 未知错误');
      }
    } catch (error: any) {
      console.error('保存批判性总结失败:', error);
      alert('保存失败: ' + error.message);
    }
  };

  // 复制文本到剪贴板
  const handleCopyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('已复制到剪贴板');
    } catch (error: any) {
      console.error('复制失败:', error);
      alert('复制失败');
    }
  };

  const handleCopySummary = () => handleCopyText(generatedSummary);

  // 生成文本语音（通用函数）
  const handleGenerateAudio = async (text: string) => {
    if (!text || text.trim().length === 0) {
      alert('没有可转换的文本内容');
      return;
    }

    const textKey = text.trim();

    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }

    if (audioCache.current.has(textKey)) {
      try {
        const cachedUrl = audioCache.current.get(textKey)!;
        const audio = new Audio(cachedUrl);
        audio.onplay = () => setCurrentAudio(audio);
        audio.onended = () => setCurrentAudio(null);
        audio.onerror = () => {
          URL.revokeObjectURL(cachedUrl);
          audioCache.current.delete(textKey);
          handleGenerateAudio(text);
        };
        await audio.play();
        return;
      } catch (error: any) {
        logger.log('缓存音频播放失败，重新生成');
      }
    }

    setIsGeneratingSpeech(true);

    try {
      const response = await fetch('/api/generate-speech-stream', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: textKey }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error('音频数据为空');
      }

      const audioBlob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);

      if (audioCache.current.size >= 20) {
        const firstKey = audioCache.current.keys().next().value;
        if (firstKey) {
          const oldUrl = audioCache.current.get(firstKey)!;
          URL.revokeObjectURL(oldUrl);
          audioCache.current.delete(firstKey);
        }
      }
      audioCache.current.set(textKey, audioUrl);

      const audio = new Audio(audioUrl);
      audio.oncanplay = () => { setIsGeneratingSpeech(false); };
      audio.onplay = () => setCurrentAudio(audio);
      audio.onended = () => setCurrentAudio(null);
      audio.onerror = () => {
        alert('音频播放失败');
        setCurrentAudio(null);
        setIsGeneratingSpeech(false);
      };

      await audio.play();
    } catch (error: any) {
      console.error('语音生成失败:', error);
      alert('语音生成失败: ' + error.message);
      setIsGeneratingSpeech(false);
    }
  };

  const handleGenerateSummaryAudio = () => handleGenerateAudio(generatedSummary);

  const handleStopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
  };

  const handleBatchRestore = async () => {
    if (selectedIds.size === 0) return;

    setIsRestoring(true);
    try {
      const result = await restoreAnnotationsBatch(Array.from(selectedIds)) as any;

      if (result.success) {
        if (pdfInfo?.fingerprint) {
          const { annotations } = await loadDeletedAnnotationsByFingerprint(pdfInfo.fingerprint) as any;
          setDeletedAnnotations(annotations);
        }
        if (onRefresh) {
          await onRefresh();
        }
        setSelectedIds(new Set());
        alert(`成功恢复 ${result.restored || selectedIds.size} 个标注`);
      } else {
        alert('恢复失败: ' + (result.error || '未知错误'));
      }
    } catch (error: any) {
      console.error('批量恢复失败:', error);
      alert('恢复失败: ' + error.message);
    } finally {
      setIsRestoring(false);
      setShowRestoreConfirm(false);
    }
  };

  const handleBatchPermanentDelete = async () => {
    if (selectedIds.size === 0) return;

    setIsPermanentDeleting(true);
    try {
      const result = await deleteAnnotationsBatch(Array.from(selectedIds), true) as any;

      if (result.success) {
        if (pdfInfo?.fingerprint) {
          const { annotations } = await loadDeletedAnnotationsByFingerprint(pdfInfo.fingerprint) as any;
          setDeletedAnnotations(annotations);
        }
        setSelectedIds(new Set());
        alert(`成功彻底删除 ${result.deleted || selectedIds.size} 个标注`);
      } else {
        alert('彻底删除失败: ' + (result.error || '未知错误'));
      }
    } catch (error: any) {
      console.error('批量彻底删除失败:', error);
      alert('彻底删除失败: ' + error.message);
    } finally {
      setIsPermanentDeleting(false);
      setShowPermanentDeleteConfirm(false);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSubTypeFilter('all');
    setSelectedIds(new Set());
    setSearchText('');
  };

  const isAllSelected = selectedIds.size > 0 && selectedIds.size === currentAnnotations.length;
  const isPartialSelected = selectedIds.size > 0 && selectedIds.size < currentAnnotations.length;

  if (!show) return null;

  return (
    <>
      <div className="annotations-list-panel-v2">
        {/* ========== 头部区域 ========== */}
        <div className="annotations-list-header-v2">
          <div className="header-top">
            <h4>标注管理中心</h4>
            <button onClick={onClose} className="close-btn">&times;</button>
          </div>

          {pdfInfo && (
            <div className="pdf-info-compact">
              <div className="pdf-filename">{pdfInfo.fileName}</div>
              <div className="pdf-meta">
                {Math.round((pdfInfo.fileSize || 0) / 1024)}KB &middot; {pdfInfo.totalPages}页 &middot;
                共{Object.keys(annotations).length}个标注
              </div>
            </div>
          )}

          <div className="tabs-container">
            <button className={`tab-btn ${activeTab === 'guide' ? 'active' : ''}`} onClick={() => handleTabChange('guide')}>
              导读 <span className="tab-count">{categorizedAnnotations.guide.length}</span>
            </button>
            <button className={`tab-btn ${activeTab === 'qa' ? 'active' : ''}`} onClick={() => handleTabChange('qa')}>
              问答 <span className="tab-count">{categorizedAnnotations.qa.length}</span>
            </button>
            <button className={`tab-btn ${activeTab === 'manual' ? 'active' : ''}`} onClick={() => handleTabChange('manual')}>
              标注 <span className="tab-count">{categorizedAnnotations.manual.length}</span>
            </button>
            <button className={`tab-btn ${activeTab === 'deleted' ? 'active' : ''}`} onClick={() => handleTabChange('deleted')}>
              回收站 <span className="tab-count">{categorizedAnnotations.deleted.length}</span>
            </button>
          </div>

          <div className="toolbar-section">
            <button className="toolbar-btn statistics-btn" onClick={() => setShowStatistics(true)} title="查看统计">统计</button>
            <button className="toolbar-btn export-btn" onClick={() => setShowExportDialog(true)} title="导出标注">导出</button>
          </div>

          <div className="filter-section">
            <div className="search-box">
              <input type="text" placeholder="搜索标注内容..." value={searchText} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)} className="search-input" />
              {searchText && (
                <button className="clear-search-btn" onClick={() => setSearchText('')} title="清除搜索">&times;</button>
              )}
            </div>

            {activeTab !== 'deleted' && (
              <div className="filter-controls">
                <select value={subTypeFilter} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSubTypeFilter(e.target.value)} className="sub-type-filter">
                  <option value="all">全部类型</option>
                  {getSubTypeOptions().map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select value={sortBy} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSortBy(e.target.value)} className="sort-filter">
                  <option value="time">按时间</option>
                  <option value="page">按页码</option>
                </select>
              </div>
            )}
          </div>

          {currentAnnotations.length > 0 && (
            <div className="batch-actions">
              <label className="select-all-checkbox">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(input: HTMLInputElement | null) => {
                    if (input) input.indeterminate = isPartialSelected;
                  }}
                  onChange={handleSelectAll}
                />
                <span>全选</span>
              </label>
              {selectedIds.size > 0 && (
                <>
                  <span className="selected-count">已选 {selectedIds.size} 个</span>
                  {activeTab === 'deleted' ? (
                    <>
                      <button onClick={() => setShowRestoreConfirm(true)} className="batch-restore-btn" disabled={isRestoring} style={{ backgroundColor: '#10b981', marginRight: '8px' }}>
                        {isRestoring ? '恢复中...' : `恢复 (${selectedIds.size})`}
                      </button>
                      <button onClick={() => setShowPermanentDeleteConfirm(true)} className="batch-delete-btn" disabled={isPermanentDeleting} style={{ backgroundColor: '#dc2626' }}>
                        {isPermanentDeleting ? '删除中...' : `彻底删除 (${selectedIds.size})`}
                      </button>
                    </>
                  ) : activeTab === 'guide' ? (
                    <>
                      <button
                        onClick={() => { setIsGeneratingSummary(false); setGeneratedSummary(''); setShowCriticalSummaryDialog(true); }}
                        className="batch-summary-btn"
                        disabled={isGeneratingSummary || selectedIds.size < 2}
                        style={{ backgroundColor: '#0D47A1', marginRight: '8px' }}
                        title={selectedIds.size < 2 ? '至少选择2条导读记录' : ''}
                      >
                        {isGeneratingSummary ? '生成中...' : `生成批判性总结 (${selectedIds.size})`}
                      </button>
                      <button onClick={handleBatchDeleteConfirm} className="batch-delete-btn" disabled={isDeleting}>
                        {isDeleting ? '删除中...' : `删除 (${selectedIds.size})`}
                      </button>
                    </>
                  ) : (
                    <button onClick={handleBatchDeleteConfirm} className="batch-delete-btn" disabled={isDeleting}>
                      {isDeleting ? '删除中...' : `删除 (${selectedIds.size})`}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* ========== 内容区域 ========== */}
        <div className="annotations-list-content-v2">
          {currentAnnotations.length === 0 ? (
            <div className="no-annotations">
              <div className="empty-icon">
                {searchText ? '🔍' : activeTab === 'deleted' ? '🗑️' : '📭'}
              </div>
              <p className="empty-title">
                {searchText ? '没有匹配的标注' :
                 activeTab === 'deleted' ? '回收站为空' : '暂无标注'}
              </p>
              <p className="empty-description">
                {searchText ? '尝试使用其他关键词搜索' :
                 (SESSION_TYPE_CONFIG as any)[activeTab]?.description || '开始创建你的第一个标注吧'}
              </p>
            </div>
          ) : (
            <div className="annotations-grid">
              {currentAnnotations.map((annotation) => {
                const isSelected = selectedIds.has(annotation.id);
                const typeConfig = getTypeConfig(annotation.subType || '', annotation) as any;

                return (
                  <div
                    key={annotation.id}
                    className={`annotation-card ${isSelected ? 'selected' : ''}`}
                    style={{ borderLeftColor: typeConfig.color }}
                  >
                    <div className="card-header">
                      <div className="header-left">
                        <label className="card-checkbox" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                          <input type="checkbox" checked={isSelected} onChange={() => handleSelectOne(annotation.id)} />
                        </label>
                        <span className="page-badge">P{annotation.page}</span>
                        <span className="type-badge" style={{ backgroundColor: typeConfig.bgColor, color: typeConfig.color, border: `1px solid ${typeConfig.color}` }}>
                          {typeConfig.icon}
                        </span>
                        {annotation.type === 'image' && <span className="image-indicator" title="图片标注">🖼️</span>}
                        {annotation.hasHighlight && <span className="highlight-indicator" title="包含高亮">&#10024;</span>}
                      </div>
                      <span className="timestamp" title={annotation.timestamp}>{formatTimestamp(annotation.timestamp)}</span>
                    </div>

                    <div className="card-content" onClick={() => onViewAnnotation(annotation)}>
                      {annotation.sessionName && <div className="session-name">{annotation.sessionName}</div>}
                      <div className="type-name">{typeConfig.shortName}</div>
                      <div className="question-text">{annotation.question}</div>
                      {annotation.text && annotation.type !== 'image' && (
                        <div className="content-preview">
                          &ldquo;{annotation.text.substring(0, 80)}{annotation.text.length > 80 ? '...' : ''}&rdquo;
                        </div>
                      )}
                      {annotation.aiQuickAnalysis && (
                        <div className="ai-analysis-preview">
                          🔍 {annotation.aiQuickAnalysis.substring(0, 100)}...
                        </div>
                      )}
                      {annotation.aiDeepAnalysis && (
                        <div className="deep-dive-indicator">
                          <span className="indicator-badge">包含深入理解</span>
                        </div>
                      )}
                    </div>

                    <div className="card-footer">
                      <button className="view-btn" onClick={() => onViewAnnotation(annotation)}>查看详情 &rarr;</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ========== 批量删除确认对话框 ========== */}
      {showBatchDeleteConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h4>确认批量删除</h4>
            <p>确定要删除选中的 <strong>{selectedIds.size}</strong> 个标注吗？</p>
            <p className="confirm-note">标注将被移至回收站，可以随时恢复</p>
            <div className="confirm-actions">
              <button onClick={cancelBatchDelete} className="cancel-btn" disabled={isDeleting}>取消</button>
              <button onClick={executeBatchDelete} className="confirm-btn" disabled={isDeleting}>{isDeleting ? '处理中...' : '确认删除'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ========== 批量恢复确认对话框 ========== */}
      {showRestoreConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h4>确认批量恢复</h4>
            <p>确定要恢复选中的 <strong>{selectedIds.size}</strong> 个标注吗？</p>
            <p className="confirm-note" style={{ color: '#10b981' }}>标注将恢复到原来的分类中</p>
            <div className="confirm-actions">
              <button onClick={() => setShowRestoreConfirm(false)} className="cancel-btn" disabled={isRestoring}>取消</button>
              <button onClick={handleBatchRestore} className="confirm-btn" disabled={isRestoring} style={{ backgroundColor: '#10b981' }}>{isRestoring ? '恢复中...' : '确认恢复'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ========== 批判性总结对话框 ========== */}
      {showCriticalSummaryDialog && (
        <div className="confirm-overlay">
          <div className="confirm-dialog" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}>
            <h4>生成批判性总结</h4>

            {isGeneratingSummary ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <div className="loading-spinner">&#9203;</div>
                <p>正在分析选中的 {selectedIds.size} 条导读记录...</p>
              </div>
            ) : generatedSummary ? (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>总结标题：</label>
                  <input type="text" value={summaryTitle} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSummaryTitle(e.target.value)} placeholder="请输入总结标题（可选）" style={{ width: '100%', padding: '8px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }} />
                </div>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', justifyContent: 'flex-start' }}>
                  <button onClick={handleCopySummary} style={{ padding: '6px 12px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px' }} title="复制到剪贴板">复制</button>
                  <button onClick={handleGenerateSummaryAudio} disabled={isGeneratingSpeech} style={{ padding: '6px 12px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '6px', cursor: isGeneratingSpeech ? 'not-allowed' : 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px', opacity: isGeneratingSpeech ? 0.6 : 1 }} title="转为语音">
                    {isGeneratingSpeech ? '生成中...' : '🔊 语音'}
                  </button>
                  {currentAudio && (
                    <button onClick={handleStopAudio} style={{ padding: '6px 12px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px' }} title="停止播放">⏹ 停止</button>
                  )}
                </div>

                <div style={{ maxHeight: '400px', overflow: 'auto', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', marginBottom: '16px' }}>
                  <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{generatedSummary}</ReactMarkdown>
                </div>

                <div className="confirm-actions">
                  <button onClick={() => { setShowCriticalSummaryDialog(false); setGeneratedSummary(''); setSummaryTitle(''); }} className="cancel-btn">取消</button>
                  <button onClick={handleSaveCriticalSummary} className="confirm-btn" style={{ backgroundColor: '#0D47A1' }}>保存批判性总结</button>
                </div>
              </>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>生成失败，请重试</p>
                <button onClick={() => setShowCriticalSummaryDialog(false)} className="cancel-btn">关闭</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== 批量彻底删除确认对话框 ========== */}
      {showPermanentDeleteConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h4>确认彻底删除</h4>
            <p>确定要<strong style={{ color: '#dc2626' }}>彻底删除</strong>选中的 <strong>{selectedIds.size}</strong> 个标注吗？</p>
            <p className="confirm-note" style={{ color: '#dc2626' }}>此操作不可恢复，数据将永久删除！</p>
            <div className="confirm-actions">
              <button onClick={() => setShowPermanentDeleteConfirm(false)} className="cancel-btn" disabled={isPermanentDeleting}>取消</button>
              <button onClick={handleBatchPermanentDelete} className="confirm-btn" disabled={isPermanentDeleting} style={{ backgroundColor: '#dc2626' }}>{isPermanentDeleting ? '删除中...' : '确认彻底删除'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ========== 内联样式 ========== */}
      <style>{`
        .annotations-list-panel-v2 { position: fixed; top: 0; right: 0; width: 450px; height: 100vh; background: white; box-shadow: -2px 0 12px rgba(0,0,0,0.15); z-index: 1000; display: flex; flex-direction: column; animation: slideInRight 0.3s ease-out; }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .annotations-list-header-v2 { padding: 1rem; border-bottom: 1px solid #e9ecef; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); }
        .header-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem; }
        .header-top h4 { margin: 0; color: #333; font-size: 1.1rem; font-weight: 600; }
        .close-btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #666; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border-radius: 50%; transition: all 0.2s; }
        .close-btn:hover { background: rgba(0,0,0,0.1); color: #333; }
        .pdf-info-compact { font-size: 12px; color: #666; margin-bottom: 1rem; padding: 0.75rem; background: white; border-radius: 6px; border-left: 3px solid #007bff; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .pdf-filename { font-weight: 600; color: #333; margin-bottom: 0.25rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .pdf-meta { font-size: 11px; color: #999; }
        .tabs-container { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.25rem; margin-bottom: 1rem; }
        .tab-btn { padding: 0.5rem 0.25rem; background: white; border: 1px solid #dee2e6; border-radius: 4px; cursor: pointer; font-size: 11px; font-weight: 500; color: #666; transition: all 0.2s; display: flex; flex-direction: column; align-items: center; gap: 0.25rem; }
        .tab-btn:hover { background: #f8f9fa; border-color: #007bff; color: #007bff; transform: translateY(-2px); }
        .tab-btn.active { background: linear-gradient(135deg, #007bff 0%, #0056b3 100%); border-color: #007bff; color: white; box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3); }
        .tab-count { font-size: 10px; padding: 2px 6px; background: rgba(0,0,0,0.1); border-radius: 10px; min-width: 20px; text-align: center; }
        .tab-btn.active .tab-count { background: rgba(255,255,255,0.3); }
        .toolbar-section { display: flex; gap: 8px; margin-bottom: 12px; }
        .toolbar-btn { flex: 1; padding: 10px 16px; border: 1px solid #e0e0e0; background: white; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 500; transition: all 0.2s; display: flex; align-items: center; justify-content: center; gap: 6px; }
        .toolbar-btn:hover { background: #f8f9fa; border-color: #2196F3; transform: translateY(-2px); box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2); }
        .filter-section { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 0.75rem; }
        .search-box { position: relative; flex: 1; }
        .search-input { width: 100%; padding: 0.6rem 2rem 0.6rem 0.75rem; border: 1px solid #dee2e6; border-radius: 6px; font-size: 13px; transition: all 0.2s; }
        .search-input:focus { outline: none; border-color: #007bff; box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1); }
        .clear-search-btn { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: #dc3545; color: white; border: none; width: 20px; height: 20px; border-radius: 50%; cursor: pointer; font-size: 14px; display: flex; align-items: center; justify-content: center; }
        .filter-controls { display: flex; gap: 0.5rem; }
        .sub-type-filter, .sort-filter { flex: 1; padding: 0.5rem; border: 1px solid #dee2e6; border-radius: 4px; font-size: 13px; background: white; cursor: pointer; }
        .batch-actions { display: flex; align-items: center; gap: 0.75rem; padding: 0.5rem 0.75rem; background: white; border-radius: 6px; border: 1px solid #dee2e6; }
        .select-all-checkbox { display: flex; align-items: center; gap: 0.5rem; font-size: 13px; cursor: pointer; user-select: none; }
        .selected-count { font-size: 12px; color: #007bff; font-weight: 600; background: #e3f2fd; padding: 2px 8px; border-radius: 10px; }
        .batch-delete-btn { background: linear-gradient(135deg, #dc3545 0%, #c82333 100%); color: white; border: none; padding: 0.4rem 0.8rem; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: 600; margin-left: auto; }
        .batch-delete-btn:disabled { background: #6c757d; cursor: not-allowed; opacity: 0.6; }
        .annotations-list-content-v2 { flex: 1; overflow-y: auto; padding: 1rem; background: #f8f9fa; }
        .no-annotations { text-align: center; padding: 4rem 2rem; color: #666; }
        .empty-icon { font-size: 4rem; margin-bottom: 1rem; opacity: 0.5; }
        .empty-title { font-size: 1.1rem; font-weight: 600; color: #333; margin-bottom: 0.5rem; }
        .empty-description { font-size: 0.9rem; color: #999; }
        .annotations-grid { display: flex; flex-direction: column; gap: 0.75rem; }
        .annotation-card { background: white; border-radius: 8px; border-left: 4px solid #007bff; padding: 0.75rem; transition: all 0.2s; cursor: pointer; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .annotation-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.1); transform: translateY(-2px); }
        .annotation-card.selected { background: #e3f2fd; box-shadow: 0 0 0 2px #2196f3; }
        .card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
        .header-left { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
        .card-checkbox { display: flex; align-items: center; }
        .page-badge { font-size: 11px; background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%); color: white; padding: 3px 8px; border-radius: 4px; font-weight: 600; }
        .type-badge { font-size: 11px; padding: 3px 8px; border-radius: 4px; font-weight: 600; }
        .timestamp { font-size: 11px; color: #999; white-space: nowrap; }
        .card-content { margin-bottom: 0.5rem; }
        .session-name { font-size: 12px; font-weight: 600; color: #666; margin-bottom: 0.25rem; }
        .type-name { font-size: 11px; color: #999; margin-bottom: 0.25rem; }
        .question-text { font-size: 14px; font-weight: 600; color: #333; margin-bottom: 0.5rem; line-height: 1.4; }
        .content-preview { font-size: 12px; color: #666; font-style: italic; line-height: 1.5; margin-bottom: 0.25rem; }
        .ai-analysis-preview { font-size: 12px; color: #007bff; background: #e3f2fd; padding: 0.5rem; border-radius: 4px; margin-top: 0.5rem; line-height: 1.4; border-left: 3px solid #2196f3; }
        .indicator-badge { display: inline-block; font-size: 11px; background: linear-gradient(135deg, #1976D2 0%, #0D47A1 100%); color: white; padding: 3px 8px; border-radius: 4px; font-weight: 600; }
        .card-footer { display: flex; justify-content: flex-end; padding-top: 0.5rem; border-top: 1px solid #f0f0f0; }
        .view-btn { background: linear-gradient(135deg, #007bff 0%, #0056b3 100%); color: white; border: none; padding: 0.4rem 0.75rem; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: 600; }
        .confirm-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.6); display: flex; align-items: center; justify-content: center; z-index: 1001; animation: fadeIn 0.2s; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .confirm-dialog { background: white; border-radius: 12px; padding: 1.5rem; width: 90%; max-width: 400px; box-shadow: 0 8px 32px rgba(0,0,0,0.2); }
        .confirm-dialog h4 { margin: 0 0 1rem 0; color: #333; font-size: 1.1rem; }
        .confirm-dialog p { margin: 0 0 1rem 0; color: #666; line-height: 1.5; }
        .confirm-note { font-size: 0.9rem; color: #999; font-style: italic; }
        .confirm-actions { display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 1.5rem; }
        .cancel-btn { background: #6c757d; color: white; border: none; padding: 0.6rem 1.2rem; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 500; }
        .cancel-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .confirm-btn { background: linear-gradient(135deg, #dc3545 0%, #c82333 100%); color: white; border: none; padding: 0.6rem 1.2rem; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 600; }
        .confirm-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        @media (max-width: 768px) { .annotations-list-panel-v2 { width: 100%; } .tabs-container { grid-template-columns: repeat(2, 1fr); } .filter-controls { flex-direction: column; } }
        .annotations-list-content-v2::-webkit-scrollbar { width: 6px; }
        .annotations-list-content-v2::-webkit-scrollbar-track { background: #f1f1f1; }
        .annotations-list-content-v2::-webkit-scrollbar-thumb { background: #888; border-radius: 3px; }
      `}</style>

      {/* 统计面板 */}
      <StatisticsPanel
        show={showStatistics}
        pdfFingerprint={pdfInfo?.fingerprint || ''}
        onClose={() => setShowStatistics(false)}
      />

      {/* 导出对话框 */}
      <ExportDialog
        show={showExportDialog}
        pdfFingerprint={pdfInfo?.fingerprint || ''}
        pdfInfo={pdfInfo as any}
        onClose={() => setShowExportDialog(false)}
      />
    </>
  );
}
